import {
    ConstrainMode,
    DefaultButton,
    Label,
    MessageBar,
    PrimaryButton,
    ScrollablePane,
    ScrollbarVisibility,
    SearchBox,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Stack,
    useTheme,
} from '@fluentui/react';
import { useBoolean } from '@uifabric/react-hooks';
import { ActivityPage, Section, SortableDetailsList } from 'components';
import { useSelector } from 'hooks';
import { useFuseSearch } from 'hooks/useFuseSearch';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import AppointmentCancellationReasonModal from './AppointmentCancellationReasonModal';
import { getAppointmentCancellationsReasons, refreshAppointmentCancellationsReasons } from './state/actions';
import IAppointmentCancellationReason from './state/model';
import {
    appointmentCancellationReasonDataAsList,
    appointmentCancellationReasonIsLoading,
    selectDentalDefaultACRObject,
} from './state/selectors';
import { completeTasksById, getUserTasksByTarget } from 'state/slices/tasks/actions';
import { UserTaskTarget } from 'state/slices/tasks/model';
import { TaskType, selectActiveAndNewUserTasksByTarget } from 'state/slices/tasks/selectors';

export default function AppointmentCancellationReasons() {
    const dispatch = useDispatch();
    const { palette } = useTheme();
    const { tenantId } = useParams<{ tenantId: string }>();
    const [isOpen, { setTrue: openModal, setFalse: closeModal }] = useBoolean(false);
    const _cancellationReasons = useSelector(appointmentCancellationReasonDataAsList);
    const isLoadingAppointmentCancellationReasons = useSelector(appointmentCancellationReasonIsLoading);

    const defaultReason = useSelector(selectDentalDefaultACRObject);

    const { setSearch, search, results } = useFuseSearch({
        list: _cancellationReasons,
        fuseOptions: { threshold: 0.1, keys: ['displayName'] },
    });

    const tasks = useSelector(selectActiveAndNewUserTasksByTarget);

    useEffect(() => {
        if (tenantId) dispatch(getAppointmentCancellationsReasons({ tenantId }));
    }, [tenantId, dispatch]);

    useEffect(() => {
        dispatch(getUserTasksByTarget({ tenantId, target: UserTaskTarget.TenantSetup }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const disableRefreshTask = tasks.find((t) => t.type === TaskType.RefreshingAppointmentCancellationReasons);
    const enableConfirmDefaultButton = tasks.some((t) => t.type === TaskType.SetRescheduleCancellationReasonId);

    return (
        <ActivityPage
            rightContent={
                <>
                    {enableConfirmDefaultButton && <DefaultButton text={'Confirm Default '} onClick={openModal} />}
                    <PrimaryButton
                        text={disableRefreshTask ? 'Cancel Refresh' : 'Refresh Data'}
                        disabled={(disableRefreshTask || isLoadingAppointmentCancellationReasons) && !disableRefreshTask}
                        onClick={() => {
                            if (disableRefreshTask) {
                                dispatch(completeTasksById({ ids: [disableRefreshTask.id] }));
                            } else {
                                dispatch(refreshAppointmentCancellationsReasons({ tenantId }));
                                setSearch('');
                            }
                        }}
                        onRenderText={(props) => {
                            return (
                                <Stack
                                    tokens={{ childrenGap: 10 }}
                                    horizontal
                                >
                                    {(isLoadingAppointmentCancellationReasons || disableRefreshTask) && <Spinner size={SpinnerSize.medium} />}
                                    <Label styles={{ root: { color: palette.white, '&:hover': { cursor: 'pointer' } }}}>{props?.text ?? ''}</Label>
                                </Stack>
                            );
                        }}
                    />

                    <SearchBox
                        placeholder="Search by display name"
                        value={search}
                        onChange={(_, value) => setSearch(value ?? '')}
                    />
                </>
            }
            title="Appointment Cancellation Reasons"
            hideFooter
            hasPageHeader
        >
            <Stack tokens={{ childrenGap: 10 }} style={{ padding: 10, display: 'flex', flex: 1 }} grow>
                {!results.length && search && <MessageBar>No results found in search.</MessageBar>}
                <div style={{ position: 'relative', display: 'flex', flex: 1 }}>
                    <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                        <Section heading="Default Appointment Cancellation Reason ">
                            {defaultReason ? (
                                <SortableDetailsList<IAppointmentCancellationReason>
                                    columns={[
                                        {
                                            key: 'id',
                                            minWidth: 90,
                                            maxWidth: 150,
                                            fieldName: 'id',

                                            name: 'ID',
                                            onRender(item) {
                                                return <span>{item?.references.athenaOne}</span>;
                                            },
                                        },
                                        {
                                            key: 'displayName',
                                            minWidth: 90,

                                            fieldName: 'displayName',
                                            name: 'Display Name',
                                        },
                                    ]}
                                    selectionMode={SelectionMode.none}
                                    items={[defaultReason]}
                                    sortOnMount
                                    sortColumns={['displayName']}
                                    initialSortDirection={'asc'}
                                    constrainMode={ConstrainMode.horizontalConstrained}
                                />
                            ) : (
                                <MessageBar>No default Appointment Cancellation Reasons found.</MessageBar>
                            )}
                        </Section>
                        <Section heading="Appointment Cancellation Reasons">
                            <SortableDetailsList<IAppointmentCancellationReason>
                                columns={[
                                    {
                                        key: 'id',
                                        minWidth: 90,
                                        maxWidth: 150,
                                        fieldName: 'id',
                                        name: 'ID',

                                        onRender(item) {
                                            return <span>{item?.references.athenaOne}</span>;
                                        },
                                        getValueKey: (item) => {
                                            return parseInt(item?.references.athenaOne ?? '').toString() ?? '';
                                        },
                                    },
                                    {
                                        key: 'displayName',
                                        minWidth: 90,

                                        fieldName: 'displayName',
                                        name: 'Display Name',
                                    },
                                ]}
                                selectionMode={SelectionMode.none}
                                items={results.length ? results : _cancellationReasons.filter((x) => x.id !== defaultReason?.id)}
                                sortOnMount
                                sortColumns={['displayName']}
                                initialSortDirection={'asc'}
                                constrainMode={ConstrainMode.horizontalConstrained}
                            />
                        </Section>
                    </ScrollablePane>
                </div>
            </Stack>
            <AppointmentCancellationReasonModal isOpen={isOpen} onDismiss={closeModal} />
        </ActivityPage>
    );
}
